/**
 * OrgSwitcher — workspace switcher chip + popover. Design contract §5.3.
 *
 * Visible in: vertical-ref, tier-1-hub modes.
 *
 * Data model for vertical-ref (cycle 2):
 *  - Current instance derived from window.location.host (domain-agnostic)
 *  - Mock list of 3 accessible instances (current + 2 hypothetical)
 *  - URL hash #test-org-search injects 9 entries to verify search-at-≥8 rule
 *
 * Border tokens: ALL borders use longhand (borderWidth/borderStyle/borderColor)
 * per contract §5.3 critical note — NEVER `border` shorthand with hover overrides.
 *
 * Refs: marketplace-broker-c2 brief §3 stage 4, design-contract.md §5.3
 */

import { useState, useRef, useEffect } from 'react';
import { t } from '../i18n';
import { useConfig } from '../config/ConfigContext';

interface InstanceEntry {
  name: string;
  slug: string;
  type: string;
  tier: string;
}

// Build mock instance list: current instance (derived from location.host) + hypothetical peers.
// Domain-agnostic: current instance name/slug come from config + window.location.host,
// never from a hardcoded .do.place string.
function buildMockInstances(currentSlug: string, currentName: string): InstanceEntry[] {
  return [
    { name: currentName, slug: currentSlug, type: 'vertical-ref', tier: 'dev' },
    { name: 'Foundation Vertical Ref', slug: 'foundation-ref-dev', type: 'vertical-ref', tier: 'dev' },
    { name: 'Equity Vertical Ref', slug: 'equity-ref-dev', type: 'vertical-ref', tier: 'dev' },
  ];
}

// 9-entry list for #test-org-search search threshold testing
function buildTestInstances(currentSlug: string, currentName: string): InstanceEntry[] {
  return [
    ...buildMockInstances(currentSlug, currentName),
    { name: 'Credit Vertical Ref', slug: 'credit-ref-dev', type: 'vertical-ref', tier: 'dev' },
    { name: 'Infrastructure Ref', slug: 'infra-ref-dev', type: 'vertical-ref', tier: 'dev' },
    { name: 'Real Estate Ref', slug: 'realestate-ref-dev', type: 'vertical-ref', tier: 'dev' },
    { name: 'Commodities Ref', slug: 'commodities-ref-dev', type: 'vertical-ref', tier: 'dev' },
    { name: 'FX Derivatives Ref', slug: 'fx-deriv-ref-dev', type: 'vertical-ref', tier: 'dev' },
    { name: 'Rates Vertical Ref', slug: 'rates-ref-dev', type: 'vertical-ref', tier: 'dev' },
  ];
}

export default function OrgSwitcher() {
  const config = useConfig();
  const mode = config.instance.mode;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // Only render for vertical-ref or tier-1-hub
  if (mode !== 'vertical-ref' && mode !== 'tier-1-hub') return null;

  // tier-1-hub: use instances from tile config (existing cycle 1 behaviour)
  // vertical-ref: use mock list derived from config + window.location
  let allInstances: InstanceEntry[];

  if (mode === 'tier-1-hub') {
    allInstances = config.catalog.tiles.flatMap((t) => t.instances ?? []);
  } else {
    // vertical-ref — domain-agnostic: slug from config, hypothetical peers mocked
    const useTestMode = typeof window !== 'undefined' &&
      window.location.hash.includes('test-org-search');
    allInstances = useTestMode
      ? buildTestInstances(config.instance.slug, config.instance.name)
      : buildMockInstances(config.instance.slug, config.instance.name);
  }

  const showSearch = allInstances.length >= 8;
  const filtered = search
    ? allInstances.filter(
        (i) =>
          i.name.toLowerCase().includes(search.toLowerCase()) ||
          i.slug.toLowerCase().includes(search.toLowerCase())
      )
    : allInstances;

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function keyHandler(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Trigger chip — longhand border tokens per §5.3 critical note */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        style={{
          background: 'transparent',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'rgba(250,247,242,0.18)',
          borderRadius: 'var(--radius-md)',
          padding: '5px var(--sp-3)',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-body-sm)',
          color: 'var(--fg-on-navy)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
        }}
      >
        {config.instance.slug}
        <span aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + var(--sp-2))',
            right: 0,
            minWidth: '280px',
            background: 'var(--bg-surface)',
            boxShadow: 'var(--shadow-3)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            zIndex: 200,
          }}
          role="listbox"
        >
          <div style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
            <p className="dp-eyebrow">{t('orgSwitcher.label')}</p>
          </div>

          {showSearch && (
            <div style={{ padding: '0 var(--sp-4) var(--sp-3)' }}>
              <input
                type="search"
                placeholder={t('orgSwitcher.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                style={{
                  width: '100%',
                  padding: '6px var(--sp-3)',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: 'var(--border-default)',
                  borderRadius: 'var(--radius-md)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body-sm)',
                }}
              />
            </div>
          )}

          <ul role="list" style={{ listStyle: 'none', padding: '0 0 var(--sp-2)', margin: 0 }}>
            {filtered.map((inst) => {
              const isActive = inst.slug === config.instance.slug;
              return (
                <li key={inst.slug}>
                  <button
                    onClick={() => setOpen(false)}
                    style={{
                      width: '100%',
                      padding: 'var(--sp-2) var(--sp-4)',
                      background: isActive ? 'var(--bg-recessed)' : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                    }}
                  >
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-body-sm)', color: 'var(--fg-1)' }}>
                      {inst.name}
                    </span>
                    <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-meta)', color: 'var(--fg-3)' }}>
                      {inst.type} · {inst.tier}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
